import { useQueries } from '@tanstack/react-query'
import { BriefcaseBusiness } from 'lucide-react'
import { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import FilterManagementDropdown from 'components/brokers/needs_attention/FilterManagementDropdown'
import { type MultipleSelectedValuesFilter } from 'components/brokers/types'
import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from 'components/reusable/table/ComboboxMultiSelectFilter'
import { getAdminEmployers, useGetAdminEmployers } from 'utils/web_api'
import { type GetAdminEmployersParams } from 'utils/web_api.schemas'

const EmployerOEEmployerFilter = (props: MultipleSelectedValuesFilter) => {
  const [query, setQuery] = useState('')
  const [queryDebounced] = useDebounceValue(query, 300)

  // Use the admin employers API with search
  const params: GetAdminEmployersParams = queryDebounced
    ? { 'filter[name]': queryDebounced, 'page[size]': 50 }
    : { 'page[size]': 50 }

  const { data, isLoading } = useGetAdminEmployers(params)
  const employers = data?.data || []

  const employerOptions: ComboboxFilterOption<string>[] =
    employers?.map((employer) => ({
      label: employer.name || employer.id,
      value: employer.id,
    })) || []

  // If the selected employers are not in the results, fetch the missing employers
  const selectedValues = props.selectedValues || []
  const employersMissingFromResults = selectedValues.filter(
    (id) => !employerOptions.some(({ value }) => value === id)
  )

  const missingEmployerResults = useQueries({
    queries: employersMissingFromResults.map((id) => ({
      queryKey: ['admin', 'employers', { 'filter[name]': id }],
      queryFn: () => getAdminEmployers({ 'filter[name]': id, 'page[size]': 1 }),
    })),
  })

  // Merge the results so that the selected employers are included in the options
  const employerOptionsIncludingSelectedEmployers = [
    ...employerOptions,
    ...(missingEmployerResults
      ?.map(({ data }) => data?.data?.[0])
      .map((employer) => ({
        label: employer?.name || '',
        value: employer?.id || '',
      })) || []),
  ]

  return (
    <div>
      <ComboboxMultiSelectFilter
        {...props}
        options={[
          {
            groupLabel: 'Employers',
            options: employerOptionsIncludingSelectedEmployers,
          },
        ]}
        placeholder='Search employers to filter...'
        onValueChange={setQuery}
        isLoading={isLoading}
      >
        <FilterManagementDropdown
          {...props}
          options={employerOptionsIncludingSelectedEmployers}
          noSelectionText='Employer'
          pluralIdentifier='Employers'
        >
          <BriefcaseBusiness size={20} />
        </FilterManagementDropdown>
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default EmployerOEEmployerFilter
